'use strict';

import { queryFirst, queryAll, addClass, removeClass, hasClass } from 'lilly/domUtil';
import { NOT_AVAILABLE_CLASS, NOT_AVAILABLE_STATUS } from './constants';
import { getProductInventoryData } from './utils/ajaxData';

const { ensureFreshCsrfToken } = require('./utils/csrfRefresh');
const $body = $('body');
const oosSheet = queryFirst('#oos-product-alternatives-sheet');
const oosContainer = oosSheet ? queryFirst('.oos-product-alternatives-container', oosSheet) : null;

let itemToReplaceData = null;
let isProcessing = false;

/**
 * Returns the attribute element for cart or mini-cart
 * @param {HTMLElement} cartItem - The cart line item element
 * @param {boolean} isCartPage - are we on the cart page
 * @param {string} attrName - The attribute name (e.g. "Size", "Color")
 * @param {string} miniCartSelector - The minicart selector class
 * @returns {HTMLElement|null}
 */
function getCartItemAttr(cartItem, isCartPage, attrName, miniCartSelector) {
    if (!cartItem) return null;
    return isCartPage
        ? queryFirst('.line-item-attributes[data-attr-name="' + attrName + '"]', cartItem)
        : queryFirst(miniCartSelector, cartItem);
}

/**
 * Updates size button availability classes based on inventory data
 * @param {HTMLElement} container - The tile container
 * @param {Object} inventory - Inventory data from getProductInventoryData
 */
function updateSizeAvailability(container, inventory) {
    if (!inventory || !inventory.variants) return;

    queryAll('.size-btn', container).forEach(btn => {
        const variantData = inventory.variants[btn.dataset.variationId];
        if (!variantData) return;

        const isNotAvailable = variantData.availabilityStatus === NOT_AVAILABLE_STATUS;
        if (isNotAvailable) {
            addClass(btn, NOT_AVAILABLE_CLASS);
        } else {
            removeClass(btn, NOT_AVAILABLE_CLASS);
        }
        btn.dataset.isNotAvailable = isNotAvailable.toString();
    });
}

/**
 * Selects a size button by value
 * @param {HTMLElement} container - The tile container
 * @param {string} sizeValue - The size value to select
 */
function selectSize(container, sizeValue) {
    if (!sizeValue) return;
    const sizeBtn = container.querySelector('.size-btn[data-attr-value="' + sizeValue + '"]');
    if (sizeBtn) sizeBtn.click();
}

/**
 * Clicks the swatch matching the given color display name
 * @param {HTMLElement} container - The tile container
 * @param {string} colorName - The color display name
 * @returns {boolean} check if a matching swatch was found and clicked
 */
function selectSwatch(container, colorName) {
    if (!colorName) return false;
    const swatches = queryAll('.js-tile-swatch.clickable-swatch', container);
    if (!swatches || swatches.length <= 1) return false;

    const matchingSwatch = swatches.find(s => s.dataset.swatchName === colorName);
    if (matchingSwatch) {
        matchingSwatch.click();
        return true;
    }
    return false;
}

/**
 * Waits for the swatch change event after clicking a swatch
 * @param {HTMLElement} container - The tile container
 * @param {HTMLElement} selectedSwatch - The selected swatch element
 * @param {string} currentColor - The color display name to select
 * @returns {Promise<void>} return
 */
function waitForSwatchChange(container, selectedSwatch, currentColor) {
    return new Promise(resolve => {
        function onSwatchChange() {
            $(oosSheet).off('hidden.bs.modal', onModalHide);
            resolve();
        }

        function onModalHide() {
            $body.off('swatchChangeEventCustom.oosSheet', onSwatchChange);
            resolve();
        }

        $body.off('swatchChangeEventCustom.oosSheet');
        $body.one('swatchChangeEventCustom.oosSheet', onSwatchChange);
        $(oosSheet).one('hidden.bs.modal', onModalHide);

        if (!selectSwatch(container, currentColor)) {
            selectedSwatch.click();
        }
    });
}

/**
 * Fetches EDD info and updates the message element
 * @param {HTMLElement} container - The OOS container element
 * @returns {Promise<void>}
 */
async function fetchEDD(container) {
    const productTile = queryFirst('.product', container);
    if (!productTile) return;

    const { pid, masterId } = productTile.dataset;
    const { eddUrl } = container.dataset;

    if (!(pid || masterId) || !eddUrl) return;

    const url = new URL(eddUrl, window.location.origin);
    url.searchParams.set('productId', pid || masterId);

    try {
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error('Edd response was not OK');
        const data = await res.json();
        if (data.eddShortMessage) {
            const eddMsg = queryFirst('.promo-edd-info', container);
            if (eddMsg) eddMsg.textContent = data.eddShortMessage;
        }
    } catch (err) {

    }
}

/**
 * Loads product tile HTML into the sheet
 * @param {string} tileUrl - The Tile-Show endpoint URL
 * @param {HTMLElement} container - The container to render the tile in
 * @param {string} currentSize - The current size from the cart item to pre-select
 * @param {string} currentColor - The current color display name from the cart item
 * @returns {Promise<void>}
 */
async function loadProductTile(tileUrl, container, currentSize, currentColor) {
    const $spinner = $(container).spinner();
    $spinner.start();

    try {
        const res = await fetch(tileUrl);
        if (!res.ok) throw new Error('response was not OK');
        const html = await res.text();
        container.innerHTML = html;

        $body.trigger('product:tileLoaded');
        $body.trigger('search:updateProducts');
        document.body.dispatchEvent(new CustomEvent('modalContentLoaded'));

        const selectedSwatch = queryFirst('.js-tile-swatch.selected', container);
        const isClickableSwatch = selectedSwatch && hasClass(selectedSwatch, 'clickable-swatch');

        if (isClickableSwatch) {
            await waitForSwatchChange(container, selectedSwatch, currentColor);
        } else {
            const masterId = queryFirst('.product-tile', container)?.dataset?.masterId;
            if (masterId) {
                const inventory = await getProductInventoryData(masterId);
                updateSizeAvailability(container, inventory);
            }
        }
        const eddPromise = fetchEDD(container);

        if (currentSize) selectSize(container, currentSize);

        await eddPromise;
    } catch (err) {
    } finally {
        $spinner.stop();
    }
}

if (oosSheet) {
    // Handle modal show to load product tile
    $(oosSheet).on('show.bs.modal', e => {
        const { relatedTarget } = e;
        if (!relatedTarget || !oosContainer) return;

        const tileUrl = relatedTarget.dataset.url;
        if (!tileUrl) return;

        const cartItem = relatedTarget.closest('.cart-item, .line-item');
        const isCartPage = !!queryFirst('.cart.cart-container');

        const sizeAttr = getCartItemAttr(cartItem, isCartPage, 'Size', '.line-item-attribute.size');
        const currentSize = sizeAttr ? sizeAttr.textContent.replace('Size:', '').trim() : '';

        const colorAttr = getCartItemAttr(cartItem, isCartPage, 'Color', '.line-item-attribute.color');
        const currentColor = colorAttr ? colorAttr.dataset.attrValue || '' : '';

        const removeBtn = cartItem ? queryFirst('.remove-product, .remove-product-btn', cartItem) : null;
        if (removeBtn) {
            const { pid, action, uuid } = removeBtn.dataset;
            itemToReplaceData = pid && action && uuid ? { pid, action, uuid } : null;
        } else {
            itemToReplaceData = null;
        }

        loadProductTile(tileUrl, oosContainer, currentSize, currentColor);
    });

    // Remove extra backdrop when sheet opens inside a parent modal (e.g. minicart)
    $(oosSheet).on('shown.bs.modal', () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        if (backdrops.length > 1) {
            backdrops[backdrops.length - 1].remove();
        }
    });

    // Handle modal hide - clean up state
    $(oosSheet).on('hidden.bs.modal', function () {
        itemToReplaceData = null;
        isProcessing = false;
    });

    // Hide sheet when CSRF error modal opens to prevent double backdrop
    $body.on('csrf:error', function () {
        if ($(oosSheet).hasClass('show')) {
            $(oosSheet).modal('hide');
        }
    });

    $(oosSheet).on('click', '.js-tile-add-to-cart', async e => {
        if (isProcessing) {
            e.preventDefault();
            return;
        }

        if (!itemToReplaceData) return;

        e.preventDefault();
        isProcessing = true;

        const tile = e.currentTarget.closest('.js-product-tile');
        if (!tile) {
            isProcessing = false;
            return;
        }

        const selectedSizeBtn = queryFirst('.size-btn.selected', tile);
        if (!selectedSizeBtn) {
            isProcessing = false;
            return;
        }

        const pid = selectedSizeBtn.dataset.variationId;
        const addToCartUrlInput = queryFirst('.add-to-cart-url', tile);
        const addToCartUrl = addToCartUrlInput ? addToCartUrlInput.value : null;
        const csrfToken = queryFirst('.js-ajax-data')?.dataset?.csrfTokenValue || '';

        if (!pid || !addToCartUrl) {
            isProcessing = false;
            return;
        }

        const { pid: removePid, action, uuid } = itemToReplaceData;

        // If the selected item is already in the cart return
        if (pid === removePid) {
            itemToReplaceData = null;
            isProcessing = false;
            $(oosSheet).modal('hide');
            return;
        }

        itemToReplaceData = null;

        $(oosSheet).modal('hide');
        $.spinner().start();

        try {
            // Add the replacement product
            const formData = new FormData();
            const csrfResult = await ensureFreshCsrfToken();
            formData.append('pid', pid);
            formData.append('csrf_token', csrfToken);

            const addRes = await fetch(addToCartUrl, { method: 'POST', body: formData });
            const addData = await addRes.json();

            if (!addData.error) {
                const url = new URL(action, window.location.origin);
                url.searchParams.set('pid', removePid);
                url.searchParams.set('uuid', uuid);
                await fetch(url.toString());
            } else {
                $.spinner().stop();
                isProcessing = false;
                itemToReplaceData = { pid: removePid, action, uuid };
                $body.trigger('cart:error', { message: addData.errorMessage || '' });
                return;
            }
        } catch (err) {
            $.spinner().stop();
            isProcessing = false;
            itemToReplaceData = { pid: removePid, action, uuid };
            return;
        }
        window.location.reload();
    });
}
